// bluaIQ — M10: Payments Module
// Payment tracking with draw schedules auto-populated from contracts/quotes
// Warns when contracts are missing payment terms

import { apiFetch } from '../utils/api'
import { formatDateOnly } from '../utils/dateOnly'
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { DollarSign, Clock, CheckCircle, AlertTriangle, Calendar, FileWarning, Bell, Download, Info } from 'lucide-react'
import { isDemoProject } from '../utils/demoData'
import { downloadCsv } from '../utils/exportCsv'
import ProFeatureGate from './ProFeatureGate'
import { useSubscriptionPlan } from '../hooks/useSubscriptionPlan'
// Financial Trust P0 (PR-F3): reuse the safe money/percent helpers so Payments
// never renders NaN/Infinity/null as money or an absurd raw percentage.
import { formatMoneyValue, formatPercentRatio } from '../utils/money'
// D-25AGO-09 / spec R-1.1.a: the settleability rules come from the SAME shared
// module the server guards with, so this UI can never offer an edge the server
// would refuse (and cannot drift away from it later).
import { isSettleableStatus, isSettleableSourceType } from '../services/paymentStateMachine'

// ── TYPES ─────────────────────────────────────────

export interface Payment {
  id: string
  amount: number
  type: string
  status: string
  dueDate?: string
  paidDate?: string
  notes?: string
  contract?: { contractorName: string; trade: string }
  source?: string // 'contract' | 'quote' | 'manual'
  // CREATE-FLOW: provenance/routing fields the GET /payments endpoint already
  // returns (Payment row columns). Needed to offer the CF-4 cancel action ONLY for
  // a SCHEDULED, draft-line-sourced row, and to build the cancel route URL.
  sourceType?: string | null
  contractId?: string | null
  milestoneLabel?: string | null
}

// ── CREATE-FLOW-4: user-facing cancel-scheduled action copy (EN/ES) ───────────
// Offered ONLY for a SCHEDULED, DRAFT_SCHEDULE_LINE payment when the backend
// readiness probe says this user+project may cancel. Soft-cancel: the row is not
// deleted — it becomes CANCELLED history and drops out of the active scheduled
// list. All copy is code-keyed; no raw server strings reach the UI.
const CF4_CANCEL_COPY = {
  cancelBtn: { en: 'Cancel', es: 'Cancelar' },
  title: { en: 'Cancel scheduled payment', es: 'Cancelar pago programado' },
  body: {
    en: 'This cancels a future scheduled payment. It is not deleted — it is kept as cancelled history and removed from active scheduled payments. It does not change Budget, Calendar/Gantt, or QuickBooks.',
    es: 'Esto cancela un pago programado futuro. No se elimina — se conserva como historial cancelado y se retira de los pagos programados activos. No cambia Presupuesto, Calendario/Gantt ni QuickBooks.',
  },
  reasonLabel: { en: 'Reason (required)', es: 'Motivo (requerido)' },
  reasonPlaceholder: { en: 'Why is this being cancelled?', es: '¿Por qué se cancela?' },
  confirmCta: { en: 'Confirm cancellation', es: 'Confirmar cancelación' },
  keepCta: { en: 'Keep', es: 'Mantener' },
  cancellingBtn: { en: 'Cancelling…', es: 'Cancelando…' },
  reasonRequired: { en: 'A reason is required to cancel.', es: 'Se requiere un motivo para cancelar.' },
  success: { en: 'Scheduled payment cancelled.', es: 'Pago programado cancelado.' },
  alreadyCancelled: { en: 'This payment was already cancelled.', es: 'Este pago ya estaba cancelado.' },
  errScope: {
    en: 'Cancellation is not enabled for this project.',
    es: 'La cancelación no está habilitada para este proyecto.',
  },
  errGeneric: {
    en: 'Could not cancel this payment. Please try again.',
    es: 'No se pudo cancelar este pago. Inténtalo de nuevo.',
  },
} as const

// ── D-25AGO-09: mark-as-paid (settlement) copy (EN/ES) ────────────────────────
// The module has told the user to "verify before marking paid" since PR-F3 — with
// no verb behind it, because no route transitioned a payment to PAID. This is
// that verb. It RECORDS a settlement; it does not move money, and the
// confirmation says so rather than letting the user infer a transfer.
const SETTLE_COPY = {
  markPaidBtn: { en: 'Mark as paid', es: 'Marcar como pagado' },
  title: { en: 'Mark this payment as paid', es: 'Marcar este pago como pagado' },
  body: {
    en: 'This records that the payment was made. It does not transfer money, and it does not update Budget, Calendar/Gantt, or QuickBooks.',
    es: 'Esto registra que el pago se realizó. No transfiere dinero, y no actualiza Presupuesto, Calendario/Gantt ni QuickBooks.',
  },
  // Spec §3.3 heading 3: the confirmation MUST name the amount, the payee and the
  // paid date, and MUST state that the action cannot be undone.
  irreversible: {
    en: 'This cannot be undone. A mistake is corrected with a new, separate record — never by un-paying this one.',
    es: 'Esto no se puede deshacer. Un error se corrige con un registro nuevo y separado — nunca despagando este.',
  },
  amountLabel: { en: 'Amount', es: 'Monto' },
  payeeLabel: { en: 'Paid to', es: 'Pagado a' },
  payeeUnknown: { en: 'No contract linked', es: 'Sin contrato vinculado' },
  paidDateLabel: { en: 'Paid date', es: 'Fecha de pago' },
  confirmCta: { en: 'Confirm payment', es: 'Confirmar pago' },
  keepCta: { en: 'Cancel', es: 'Cancelar' },
  workingBtn: { en: 'Saving…', es: 'Guardando…' },
  errConflict: {
    en: 'This payment was already marked paid. Reload to see its current state.',
    es: 'Este pago ya se marcó como pagado. Recarga para ver su estado actual.',
  },
  errNotSettleable: {
    en: 'Only a pending or overdue payment can be marked paid.',
    es: 'Solo un pago pendiente o vencido puede marcarse como pagado.',
  },
  errSource: {
    en: 'A scheduled payment cannot be marked paid from here.',
    es: 'Un pago programado no puede marcarse como pagado desde aquí.',
  },
  errGeneric: {
    en: 'Could not mark this payment as paid. Please try again.',
    es: 'No se pudo marcar este pago como pagado. Inténtalo de nuevo.',
  },
  // The timeline groups payments by contractor; rows with no contract used to be
  // dropped entirely and were therefore unreachable. They now get their own,
  // honestly-labelled group instead of being invisible.
  noContractGroup: { en: 'No contract linked', es: 'Sin contrato vinculado' },
} as const

// Map a settlement error CODE to localized copy — never the raw server string.
const localizeSettleError = (code: string, status: number, isEs: boolean): string => {
  const L = (k: { en: string; es: string }) => (isEs ? k.es : k.en)
  switch (code) {
    case 'PAYMENT_ALREADY_SETTLED':
      return L(SETTLE_COPY.errConflict)
    case 'PAYMENT_NOT_SETTLEABLE':
      return L(SETTLE_COPY.errNotSettleable)
    case 'INVALID_PAYMENT_SOURCE':
      return L(SETTLE_COPY.errSource)
    default:
      return status === 409 ? L(SETTLE_COPY.errConflict) : L(SETTLE_COPY.errGeneric)
  }
}

/**
 * Mint the idempotency key for ONE settlement attempt. Minted when the
 * confirmation opens and reused across every retry of that same confirmation, so
 * a double-click or a retried request settles ONCE and returns the same row. A
 * key minted per-request would defeat the guarantee entirely.
 */
function mintSettlementKey(paymentId: string): string {
  const rand =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`
  return `settle-${paymentId}-${rand}`
}

// Map a cancel-scheduled error response code to localized copy. 409 already-
// cancelled is surfaced as a NEUTRAL message (not a hard error). Unknown codes
// fall back to the generic message; never the raw server string.
const localizeCancelError = (code: string, isEs: boolean): { neutral: boolean; message: string } => {
  const L = (k: { en: string; es: string }) => (isEs ? k.es : k.en)
  switch (code) {
    case 'PAYMENT_ALREADY_CANCELLED':
      return { neutral: true, message: L(CF4_CANCEL_COPY.alreadyCancelled) }
    case 'MISSING_CANCELLATION_REASON':
      return { neutral: false, message: L(CF4_CANCEL_COPY.reasonRequired) }
    case 'SCHEDULED_PAYMENT_CREATE_DISABLED':
    case 'SCHEDULED_PAYMENT_CREATE_NOT_ALLOWED_FOR_SCOPE':
      return { neutral: false, message: L(CF4_CANCEL_COPY.errScope) }
    default:
      return { neutral: false, message: L(CF4_CANCEL_COPY.errGeneric) }
  }
}

interface MissingTerms {
  contractorName: string
  trade: string
  totalAmount: number
  contractId: string
}

// Payment type labels live in i18n under payments.type.*

// ── DEMO DATA ─────────────────────────────────────

const DEMO_PAYMENTS: Payment[] = [
  // Foundation — Solid Ground LLC (contract: 33/33/34 draw schedule)
  { id: 'pay-1', amount: 6105, type: 'DEPOSIT', status: 'PAID', dueDate: '2026-02-20', paidDate: '2026-02-20',
    notes: 'Anticipo 33% — Cimentación', contract: { contractorName: 'Solid Ground LLC', trade: 'Foundation' }, source: 'contract' },
  { id: 'pay-2', amount: 6105, type: 'PROGRESS', status: 'PAID', dueDate: '2026-03-01', paidDate: '2026-03-02',
    notes: 'Avance 33% — Vaciado completado', contract: { contractorName: 'Solid Ground LLC', trade: 'Foundation' }, source: 'contract' },
  { id: 'pay-3', amount: 6290, type: 'FINAL', status: 'PAID', dueDate: '2026-03-05', paidDate: '2026-03-05',
    notes: 'Pago final 34% — Inspección aprobada', contract: { contractorName: 'Solid Ground LLC', trade: 'Foundation' }, source: 'contract' },

  // Framing — Mendez Framing LLC (contract: 40/30/30)
  { id: 'pay-4', amount: 16800, type: 'DEPOSIT', status: 'PAID', dueDate: '2026-03-08', paidDate: '2026-03-08',
    notes: 'Anticipo 40% — Inicio de framing', contract: { contractorName: 'Mendez Framing LLC', trade: 'Framing' }, source: 'contract' },
  { id: 'pay-5', amount: 12600, type: 'PROGRESS', status: 'PAID', dueDate: '2026-03-18', paidDate: '2026-03-19',
    notes: 'Avance 30% — Walls up', contract: { contractorName: 'Mendez Framing LLC', trade: 'Framing' }, source: 'contract' },
  { id: 'pay-6', amount: 12600, type: 'FINAL', status: 'PENDING', dueDate: '2026-03-28',
    notes: 'Pago final 30% — Después de inspección framing', contract: { contractorName: 'Mendez Framing LLC', trade: 'Framing' }, source: 'contract' },

  // Roofing — Beacon (material purchase, COD)
  { id: 'pay-7', amount: 4557.33, type: 'MATERIAL', status: 'PAID', dueDate: '2026-03-12', paidDate: '2026-03-12',
    notes: 'Material de techo — Beacon Roofing (COD)', contract: { contractorName: 'Beacon Roofing', trade: 'Roofing' }, source: 'quote' },

  // Plumbing — Torres Plumbing (pending contract, draw: 50/50)
  { id: 'pay-8', amount: 7250, type: 'DEPOSIT', status: 'PENDING', dueDate: '2026-03-30',
    notes: 'Anticipo 50% — Inicio rough-in', contract: { contractorName: 'Torres Plumbing', trade: 'Plumbing' }, source: 'contract' },
  { id: 'pay-9', amount: 7250, type: 'FINAL', status: 'PENDING', dueDate: '2026-04-12',
    notes: 'Pago final 50% — Después de inspección', contract: { contractorName: 'Torres Plumbing', trade: 'Plumbing' }, source: 'contract' },

  // Material purchases
  { id: 'pay-10', amount: 3082.42, type: 'MATERIAL', status: 'PAID', dueDate: '2026-03-18', paidDate: '2026-03-18',
    notes: 'Lumber — 84 Lumber Garland', contract: { contractorName: '84 Lumber', trade: 'Materials' }, source: 'quote' },
  { id: 'pay-11', amount: 1576.99, type: 'MATERIAL', status: 'PAID', dueDate: '2026-03-15', paidDate: '2026-03-15',
    notes: 'Insulation — Home Depot Pro', contract: { contractorName: 'Home Depot Pro', trade: 'Materials' }, source: 'quote' },

  // Electrical — NO payment terms yet
  { id: 'pay-12', amount: 12000, type: 'DEPOSIT', status: 'PENDING', dueDate: '2026-04-05',
    notes: 'Anticipo — Pendiente definir calendario de pagos', contract: { contractorName: 'Spark Electric Co', trade: 'Electrical' }, source: 'manual' },
]

// Contracts missing payment terms — should prompt user to set them up
const DEMO_MISSING_TERMS: MissingTerms[] = [
  { contractorName: 'DFW Drywall Pros', trade: 'Drywall', totalAmount: 16500, contractId: 'ct-drywall' },
  { contractorName: 'Pro Paint DFW', trade: 'Painting', totalAmount: 8500, contractId: 'ct-paint' },
]


// ── D-25AGO-09: ONE PAYMENT ROW IN THE TIMELINE ──────────────────────────────
//
// Extracted from the module so it can be MOUNTED AND MEASURED in a real Chromium
// browser (tests/ct/paymentTimelineRow.ct.tsx). Codex measured the Spanish
// "Marcar como pagado" button rendering OUTSIDE the visible 390px container, and
// a Tailwind-class assertion cannot see that — jsdom computes no layout, so only
// a real browser can say whether a button is clipped. The component takes plain
// props (no Clerk, router, i18n or query hooks) for exactly the reason the
// client_question card does: that is what makes it mountable standalone.
//
// THE LAYOUT RULE. Below `sm` the row WRAPS: the amount keeps the right edge of
// the first line and the action drops onto its own full-width line beneath it.
// Above `sm` it is the single row it always was. Nothing is hidden, nothing is
// truncated away, and the Spanish copy is NOT shortened to hide the overflow —
// the label is the honest one in both languages.
export interface PaymentTimelineRowProps {
  payment: Payment
  idx: number
  isEs: boolean
  typeLabel: string
  sourceLabel: string
  sourceKind: 'contract' | 'quote' | 'manual'
  paidLabel: string
  dueLabel: string
  formatDate: (d?: string) => string
  settleable: boolean
  isSettleTarget: boolean
  settleError: string | null
  settleSubmitting: boolean
  onOpenSettle: () => void
  onCancelSettle: () => void
  onConfirmSettle: () => void
}

export function PaymentTimelineRow({
  payment, idx, isEs, typeLabel, sourceLabel, sourceKind, paidLabel, dueLabel, formatDate,
  settleable, isSettleTarget, settleError, settleSubmitting,
  onOpenSettle, onCancelSettle, onConfirmSettle,
}: PaymentTimelineRowProps) {
  const isPaid = payment.status === 'PAID'
  const isOverdue = payment.status === 'OVERDUE'

  return (
    <div data-testid={`payment-row-${payment.id}`} className="border-t border-[#F2F0EB]">
      {/* flex-wrap + gap-y is the whole responsive fix: at 390px the action no
          longer has to share one line with the dot, label, chip, date and
          amount, so it can never be pushed past the container's right edge. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2 pl-8">
        {/* Timeline dot + connector */}
        <div className="flex flex-col items-center w-[16px] flex-shrink-0">
          {idx > 0 && <div className="w-[1.5px] h-[8px] bg-[#ECEAE4] -mt-2" />}
          <div
            className="w-[10px] h-[10px] rounded-full border-2 flex items-center justify-center"
            style={{
              borderColor: isPaid ? '#22C55E' : isOverdue ? '#EF4444' : '#06539B',
              background: isPaid ? '#22C55E' : 'white',
            }}
          >
            {isPaid && <span className="text-white text-[6px]">✓</span>}
          </div>
        </div>

        {/* Payment info — min-w-0 so a long label shrinks instead of pushing the
            row wider than its container. */}
        <div className="flex-1 min-w-0 basis-[7rem]">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[11px] font-[700] text-[#1C1A17] truncate">{typeLabel}</span>
            <span data-testid="payment-source-chip" className={`text-[8px] font-[800] px-1.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 ${
              sourceKind === 'contract' ? 'bg-purple-50 text-purple-600' :
              sourceKind === 'quote' ? 'bg-blue-50 text-blue-600' :
              'bg-gray-50 text-gray-600'
            }`}>
              {sourceLabel}
            </span>
          </div>
          {payment.notes && (
            <div className="text-[9px] font-[500] text-[#9E9890] mt-0.5 truncate">{payment.notes}</div>
          )}
        </div>

        {/* Date */}
        <div className="flex items-center gap-1 text-[10px] font-[600] text-[#9E9890] whitespace-nowrap flex-shrink-0">
          <Calendar size={10} />
          {isPaid ? (
            <span className="text-[#22C55E]">{paidLabel} {formatDate(payment.paidDate)}</span>
          ) : (
            <span className={isOverdue ? 'text-red-500' : ''}>{dueLabel} {formatDate(payment.dueDate)}</span>
          )}
        </div>

        {/* Amount */}
        <div
          data-testid={`payment-amount-${payment.id}`}
          className={`text-[12px] font-[800] w-[88px] text-right flex-shrink-0 ${isPaid ? 'text-[#9E9890] line-through' : 'text-[#1C1A17]'}`}
        >
          {formatMoneyValue(payment.amount, isEs)}
        </div>

        {/* D-25AGO-09 — mark-as-paid trigger. A PAID row shows NO action at all:
            PAID is terminal and an "undo paid" affordance is PROHIBITED in any
            form. Full width below `sm` so it wraps onto its own line rather than
            overflowing the container. */}
        {settleable && !isSettleTarget && (
          <button
            type="button"
            data-testid={`payment-mark-paid-btn-${payment.id}`}
            onClick={onOpenSettle}
            className="w-full sm:w-auto min-h-[44px] whitespace-nowrap rounded-full border border-[#B7E4C7] bg-[#ECFDF5] px-3 text-[11px] font-[800] text-[#15803D]"
          >
            {isEs ? SETTLE_COPY.markPaidBtn.es : SETTLE_COPY.markPaidBtn.en}
          </button>
        )}
      </div>

      {/* Inline confirmation (only for the targeted row). Spec §3.3 heading 3:
          names the amount, the payee and the paid date, and states that the
          action cannot be undone. */}
      {settleable && isSettleTarget && (
        <div
          data-testid={`payment-settle-confirm-${payment.id}`}
          className="mx-4 mb-2 ml-8 space-y-1.5 rounded-lg border border-[#C9E6D2] bg-[#F6FCF8] px-3 py-2.5"
        >
          <p className="text-[11px] font-[900] text-[#1C1A17]">
            {isEs ? SETTLE_COPY.title.es : SETTLE_COPY.title.en}
          </p>
          <p className="text-[10px] font-[600] text-[#6B655C] leading-relaxed">
            {isEs ? SETTLE_COPY.body.es : SETTLE_COPY.body.en}
          </p>

          <div data-testid={`payment-settle-figures-${payment.id}`} className="space-y-0.5 pt-0.5">
            <div className="flex items-baseline justify-between gap-2 text-[11px] font-[700] text-[#1C1A17]">
              <span>{isEs ? SETTLE_COPY.amountLabel.es : SETTLE_COPY.amountLabel.en}</span>
              <span className="font-[900]">{formatMoneyValue(payment.amount, isEs)}</span>
            </div>
            <div className="flex items-baseline justify-between gap-2 text-[10px] font-[600] text-[#6B655C]">
              <span>{isEs ? SETTLE_COPY.payeeLabel.es : SETTLE_COPY.payeeLabel.en}</span>
              <span className="font-[800] text-[#1C1A17] truncate max-w-[55%] text-right">
                {payment.contract?.contractorName
                  || (isEs ? SETTLE_COPY.payeeUnknown.es : SETTLE_COPY.payeeUnknown.en)}
              </span>
            </div>
            <div className="flex items-baseline justify-between gap-2 text-[10px] font-[600] text-[#6B655C]">
              <span>{isEs ? SETTLE_COPY.paidDateLabel.es : SETTLE_COPY.paidDateLabel.en}</span>
              <span className="font-[800] text-[#1C1A17]">
                {new Date().toLocaleDateString(isEs ? 'es-MX' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
              </span>
            </div>
          </div>

          <p className="text-[10px] font-[800] text-[#9E6B00] leading-relaxed">
            {isEs ? SETTLE_COPY.irreversible.es : SETTLE_COPY.irreversible.en}
          </p>

          {settleError && (
            <p data-testid={`payment-settle-error-${payment.id}`} className="text-[10px] font-[800] text-[#B4232A]">
              {settleError}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-2 pt-0.5">
            <button
              type="button"
              data-testid={`payment-settle-confirm-btn-${payment.id}`}
              disabled={settleSubmitting}
              onClick={onConfirmSettle}
              className="w-full min-h-[44px] rounded-full bg-[#15803D] px-3 text-[11px] font-[800] text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {settleSubmitting
                ? (isEs ? SETTLE_COPY.workingBtn.es : SETTLE_COPY.workingBtn.en)
                : (isEs ? SETTLE_COPY.confirmCta.es : SETTLE_COPY.confirmCta.en)}
            </button>
            <button
              type="button"
              data-testid={`payment-settle-cancel-btn-${payment.id}`}
              disabled={settleSubmitting}
              onClick={onCancelSettle}
              className="w-full min-h-[44px] rounded-full border border-[#D6CFC4] bg-white px-3 text-[11px] font-[800] text-[#6B655C]"
            >
              {isEs ? SETTLE_COPY.keepCta.es : SETTLE_COPY.keepCta.en}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── COMPONENT ─────────────────────────────────────

export default function PaymentsModule() {
  const { getToken } = useAuth()
  const { projectId } = useParams()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  const [showMissing, setShowMissing] = useState(true)
  // Plan gating is server-authoritative via /api/subscription. Falls
  // back to STARTER while loading or on error — never to PRO. The
  // legacy `const demoPlan = 'PRO'` constant was removed; demo
  // toggling still works inside `useSubscriptionPlan` when isDemo.
  const { effectivePlan } = useSubscriptionPlan()

  const { data: apiPayments, isLoading } = useQuery<Payment[]>({
    queryKey: ['payments', projectId],
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/payments`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => {
      if (!r.ok) throw new Error('API unavailable')
      return r.json()
    }) },
    retry: false,
  })

  // PR-F3: read-only contract count so the empty state can be HONEST — "0 scheduled
  // payments" must NOT imply "no commitments" when contracts exist. This is a
  // read-only informational fetch; it does NOT connect modules or create payments.
  const { data: apiContracts } = useQuery<{ id: string }[]>({
    queryKey: ['contracts', projectId],
    queryFn: async () => { const token = await getToken(); return apiFetch(`/api/projects/${projectId}/contracts`, { headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => r.ok ? r.json() : []) },
    retry: false,
  })

  // CREATE-FLOW-4: read-only readiness probe — decides whether to offer the gated
  // per-row "Cancel" action for SCHEDULED rows. Returns booleans + a code only; no
  // secrets. `canCancel` defaults false (flag OFF and/or not scoped-allowlisted).
  const queryClient = useQueryClient()
  const { data: scheduleReadiness } = useQuery<{
    enabled: boolean
    allowedForScope: boolean
    canCreate: boolean
    canCancel: boolean
    reasonCode: string | null
  }>({
    queryKey: ['schedule-readiness', projectId],
    queryFn: async () => {
      const token = await getToken()
      return apiFetch(`/api/projects/${projectId}/scheduled-payments/readiness`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      }).then((r) => (r.ok ? r.json() : { enabled: false, allowedForScope: false, canCreate: false, canCancel: false, reasonCode: null }))
    },
    enabled: !isDemoProject(projectId) && !!projectId,
    retry: false,
  })

  // CREATE-FLOW-4 cancel-action state: which scheduled row is being cancelled, the
  // required human reason, a submitting flag, and a per-row result banner.
  // D-25AGO-09 settlement state. ONE target at a time, and the idempotency key is
  // minted WITH the target (see mintSettlementKey) so every retry of the same
  // confirmation carries the same key.
  const [settleTarget, setSettleTarget] = useState<{ id: string; key: string } | null>(null)
  const [settleSubmitting, setSettleSubmitting] = useState(false)
  const [settleError, setSettleError] = useState<{ paymentId: string; message: string } | null>(null)

  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null)
  const [cancelReason, setCancelReason] = useState('')
  const [cancelSubmitting, setCancelSubmitting] = useState(false)
  const [cancelResult, setCancelResult] = useState<
    { paymentId: string; neutral: boolean; message: string } | null
  >(null)

  // D-25AGO-09 — open/close the settlement confirmation. The key is minted HERE,
  // once per confirmation, and reused by every attempt from it.
  const openSettle = (paymentId: string) => {
    setSettleTarget({ id: paymentId, key: mintSettlementKey(paymentId) })
    setSettleError(null)
  }
  const closeSettle = () => {
    setSettleTarget(null)
    setSettleError(null)
  }

  // D-25AGO-09 — mark a PENDING/OVERDUE payment as PAID. This records that the
  // payment was made; it does not move money. PAID is terminal, so there is no
  // matching "un-pay" handler anywhere in this module — deliberately.
  const runSettle = async (payment: Payment) => {
    if (settleSubmitting || !settleTarget || settleTarget.id !== payment.id) return
    setSettleSubmitting(true)
    setSettleError(null)
    try {
      const token = await getToken()
      const res = await apiFetch(`/api/projects/${projectId}/payments/${payment.id}/settle`, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
        body: JSON.stringify({ idempotencyKey: settleTarget.key }),
      })
      const body = await res.json().catch(() => ({} as any))
      if (!res.ok) {
        setSettleError({
          paymentId: payment.id,
          message: localizeSettleError(typeof body?.code === 'string' ? body.code : '', res.status, isEs),
        })
        return
      }
      closeSettle()
      queryClient.invalidateQueries({ queryKey: ['payments', projectId] })
    } catch {
      setSettleError({ paymentId: payment.id, message: isEs ? SETTLE_COPY.errGeneric.es : SETTLE_COPY.errGeneric.en })
    } finally {
      setSettleSubmitting(false)
    }
  }

  const openCancel = (paymentId: string) => {
    setCancelTargetId(paymentId)
    setCancelReason('')
    setCancelResult(null)
  }
  const closeCancel = () => {
    setCancelTargetId(null)
    setCancelReason('')
  }

  // CREATE-FLOW-4: soft-cancel a SCHEDULED payment via the gated cancel route.
  // Requires a non-empty reason (also enforced server-side). On success the row is
  // refetched → becomes CANCELLED → drops out of the active scheduled list. A 409
  // duplicate-cancel shows a NEUTRAL message. Never deletes a row.
  const runCancelScheduled = async (payment: Payment) => {
    if (cancelSubmitting) return
    const reason = cancelReason.trim()
    if (!reason) {
      setCancelResult({ paymentId: payment.id, neutral: false, message: isEs ? CF4_CANCEL_COPY.reasonRequired.es : CF4_CANCEL_COPY.reasonRequired.en })
      return
    }
    if (!payment.contractId) {
      setCancelResult({ paymentId: payment.id, neutral: false, message: isEs ? CF4_CANCEL_COPY.errGeneric.es : CF4_CANCEL_COPY.errGeneric.en })
      return
    }
    setCancelSubmitting(true)
    try {
      const token = await getToken()
      const res = await apiFetch(
        `/api/projects/${projectId}/contracts/${payment.contractId}/payments/${payment.id}/cancel-scheduled`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ reason }),
        },
      )
      const data = await res.json().catch(() => ({}))
      if (res.ok) {
        setCancelTargetId(null)
        setCancelReason('')
        setCancelResult({ paymentId: payment.id, neutral: false, message: isEs ? CF4_CANCEL_COPY.success.es : CF4_CANCEL_COPY.success.en })
        queryClient.invalidateQueries({ queryKey: ['payments', projectId] })
      } else {
        const code = typeof data?.code === 'string' ? data.code : ''
        const mapped = localizeCancelError(code, isEs)
        // A duplicate 409 means the row is already gone from active scheduling —
        // refetch so the UI converges, and surface the neutral message.
        if (mapped.neutral) queryClient.invalidateQueries({ queryKey: ['payments', projectId] })
        setCancelResult({ paymentId: payment.id, neutral: mapped.neutral, message: mapped.message })
        if (mapped.neutral) setCancelTargetId(null)
      }
    } catch {
      setCancelResult({ paymentId: payment.id, neutral: false, message: isEs ? CF4_CANCEL_COPY.errGeneric.es : CF4_CANCEL_COPY.errGeneric.en })
    } finally {
      setCancelSubmitting(false)
    }
  }

  const payments = Array.isArray(apiPayments) ? apiPayments : (isDemoProject(projectId) ? DEMO_PAYMENTS : [])
  const contractCount = Array.isArray(apiContracts) ? apiContracts.length : 0
  const hasContractsNoPayments = payments.length === 0 && contractCount > 0

  // PR-F3: honest source resolution. The DB Payment row has NO `source` column, so
  // real payments arrive without one — derive it: a row tied to a contract (or the
  // auto-created DRAW_SCHEDULE type) is contract-sourced, not "manual". Auto-created
  // draw-schedule rows must be flagged for review (they are not user-confirmed).
  const paymentSource = (p: Payment): 'contract' | 'quote' | 'manual' => {
    if (p.source === 'contract' || p.source === 'quote' || p.source === 'manual') return p.source
    if (p.contract || p.type === 'DRAW_SCHEDULE') return 'contract'
    return 'manual'
  }
  const hasAutoCreated = payments.some(p => p.type === 'DRAW_SCHEDULE' || (!p.source && !!p.contract))
  // Only show DEMO_MISSING_TERMS on demo projects — real projects must not show mock
  // contracts that don't exist in the DB (Contracts module would say "0 contratos").
  const missingTerms: MissingTerms[] = isDemoProject(projectId) && !apiPayments ? DEMO_MISSING_TERMS : []

  if (isLoading) return <div className="p-5 animate-pulse"><div className="h-64 bg-[#ECEAE4] rounded-xl" /></div>

  const overdue = payments.filter(p => p.status === 'OVERDUE')
  const pending = payments.filter(p => p.status === 'PENDING')
  const paid = payments.filter(p => p.status === 'PAID')
  // NBA-13C reader guard: SCHEDULED is a future planning state. No SCHEDULED rows
  // exist yet — they are created only by a future, separately-authorized
  // schedule-payments endpoint. SCHEDULED must NEVER blend into the PAID / PENDING /
  // OVERDUE counters or the contractor due/paid timeline; it is surfaced in its own
  // clearly-labelled section so it can never read as a real due/paid obligation.
  const scheduled = payments.filter(p => p.status === 'SCHEDULED')
  // CF-3: CANCELLED is a soft-cancelled scheduled payment — historical, NOT a real
  // due/paid obligation. Like SCHEDULED, it is excluded from realPayments so it
  // never enters the contractor due/paid timeline or the PAID/PENDING/OVERDUE
  // counters (those already match on explicit status, so CANCELLED never inflates a
  // paid/pending/overdue total).
  const realPayments = payments.filter(p => p.status !== 'SCHEDULED' && p.status !== 'CANCELLED')

  const totalPaid = paid.reduce((s, p) => s + p.amount, 0)
  const totalPending = pending.reduce((s, p) => s + p.amount, 0)
  const totalOverdue = overdue.reduce((s, p) => s + p.amount, 0)
  const totalScheduled = scheduled.reduce((s, p) => s + p.amount, 0)
  const totalMissing = missingTerms.reduce((s, m) => s + m.totalAmount, 0)

  // Group payments by contractor for timeline view (SCHEDULED excluded — see guard above)
  const contractors = [...new Set(realPayments.map(p => p.contract?.contractorName).filter(Boolean))]

  // D-25AGO-09 — a payment with NO linked contract used to be dropped by this
  // grouping entirely: `filter(Boolean)` removed it from `contractors`, so it
  // appeared in the KPI counters but in no row of the timeline. That was
  // survivable while the timeline was read-only; it is not survivable now that
  // the timeline row carries the mark-as-paid verb, because it would leave every
  // manually-created payment permanently unsettleable. The rows get their own
  // group, labelled for what they are rather than given a fake contractor.
  const byDueDate = (a: Payment, b: Payment) => (a.dueDate || '').localeCompare(b.dueDate || '')
  const unlinkedPayments = realPayments.filter(p => !p.contract?.contractorName).sort(byDueDate)
  const timelineGroups: { key: string; label: string; trade: string; rows: Payment[] }[] = [
    ...contractors.map(name => {
      const rows = realPayments.filter(p => p.contract?.contractorName === name).sort(byDueDate)
      return { key: name as string, label: name as string, trade: rows[0]?.contract?.trade || '', rows }
    }),
    ...(unlinkedPayments.length > 0
      ? [{
          key: '__no_contract__',
          label: isEs ? SETTLE_COPY.noContractGroup.es : SETTLE_COPY.noContractGroup.en,
          trade: '',
          rows: unlinkedPayments,
        }]
      : []),
  ]

  // PR3 (C5): due/paid dates are calendar days — never shift them a day.
  const formatDate = (d?: string) => formatDateOnly(d, isEs ? 'es-MX' : 'en-US', { month: 'short', day: 'numeric' })

  return (
    <div className="p-5 max-w-5xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">
            {t('payments.title')}
          </h2>
          <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
            {payments.length} {t('payments.scheduledPayments')} · {t('payments.fromContractsQuotes')}
          </p>
        </div>
        {payments.length > 0 && (
          <button
            onClick={() => downloadCsv({
              filename: `pagos-proyecto-${projectId}`,
              headers: [t('payments.csv.contractor'), t('payments.csv.trade'), t('payments.csv.type'), t('payments.csv.amount'), t('payments.csv.status'), t('payments.csv.dueDate'), t('payments.csv.paidDate'), t('payments.csv.source'), t('payments.csv.notes')],
              rows: payments.map(p => [p.contract?.contractorName || '', p.contract?.trade || '', p.type, p.amount, p.status, p.dueDate || '', p.paidDate || '', p.source || '', p.notes || ''])
            })}
            className="flex items-center gap-1.5 border border-[#ECEAE4] text-[#1C1A17] text-[13px] font-[800] px-4 py-2.5 rounded-full hover:bg-[#F2F0EB] transition-colors"
          >
            <Download size={14} />
            {t('payments.downloadCsv')}
          </button>
        )}
      </div>

      {/* PR-F3: honest trust note — payments are not fully user-confirmed from
          contracts and are not connected to Calendar/Gantt yet. No silent writes. */}
      <div data-testid="payments-trust-note" className="flex items-start gap-2 text-[10px] font-[600] text-[#9E6B00] bg-[#FEF7E6] border border-[#F5E2B8] rounded-lg px-3 py-2 leading-relaxed">
        <Info size={12} className="flex-shrink-0 mt-0.5" />
        <span>
          {isEs
            ? 'Los contratos se registran por separado. El calendario de pagos aún no está totalmente conectado ni vinculado a Calendar/Gantt.'
            : 'Contracts are tracked separately. The payment schedule is not fully connected yet and is not connected to Calendar/Gantt yet.'}
        </span>
      </div>

      {/* PR-F3: honest empty state. 0 scheduled payments must NOT imply no
          commitments when contracts exist. */}
      {payments.length === 0 && (
        <div data-testid="payments-empty-state" className="bg-white border border-[#ECEAE4] rounded-xl p-8 text-center space-y-2">
          <DollarSign size={32} className="mx-auto text-[#ECEAE4]" />
          {hasContractsNoPayments ? (
            <>
              <p data-testid="payments-contracts-exist-note" className="text-[13px] font-[800] text-[#1C1A17]">
                {isEs
                  ? 'Existen contratos, pero el calendario de pagos aún no está conectado.'
                  : 'Contracts exist, but payment schedule is not connected yet.'}
              </p>
              <p className="text-[11px] font-[600] text-[#9E9890] max-w-md mx-auto">
                {isEs
                  ? `Hay ${contractCount} contrato(s) registrados por separado. Esto no significa que no haya compromisos.`
                  : `${contractCount} contract(s) are tracked separately. This does not mean there are no commitments.`}
              </p>
              <button
                onClick={() => navigate(`/projects/${projectId}/contracts`)}
                className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-[800] text-[#06539B] hover:bg-[#06539B]/5 border border-[#06539B]/30 rounded-full px-4 py-2 transition-colors"
              >
                {isEs ? 'Ver contratos →' : 'View contracts →'}
              </button>
            </>
          ) : (
            <p data-testid="payments-no-payments-note" className="text-[13px] font-[800] text-[#9E9890]">
              {isEs ? 'Aún no hay pagos programados.' : 'No scheduled payments yet.'}
            </p>
          )}
        </div>
      )}

      {/* Missing payment terms warning — PRO+ feature */}
      {missingTerms.length > 0 && showMissing && (
      <ProFeatureGate requiredPlan="PRO" currentPlan={effectivePlan} featureName="Payment Alerts" featureNameEs="Alertas de Pagos" isEs={isEs} mode="inline">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <FileWarning size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-[13px] font-[800] text-amber-800">
                {t('payments.missing.title', { count: missingTerms.length })}
              </h3>
              <p className="text-[11px] font-[500] text-amber-700 mt-1">
                {t('payments.missing.description')}
              </p>
              <div className="mt-3 space-y-2">
                {missingTerms.map(m => (
                  <div key={m.contractId} className="flex items-center gap-3 bg-white rounded-lg px-3 py-2 border border-amber-100">
                    <AlertTriangle size={12} className="text-amber-500 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-[11px] font-[800] text-[#1C1A17]">{m.contractorName}</span>
                      <span className="text-[10px] font-[600] text-[#9E9890] ml-2">{m.trade} · {formatMoneyValue(m.totalAmount, isEs)}</span>
                    </div>
                    <button
                      onClick={() => navigate(`/projects/${projectId}/contracts`)}
                      className="text-[10px] font-[800] text-amber-700 bg-amber-100 px-3 py-1.5 rounded-full hover:bg-amber-200 transition-colors">
                      {t('payments.missing.setup')}
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-[10px] font-[600] text-amber-600 mt-2">
                {/* Template already prefixes "$", so pass a guarded number only (no symbol). */}
                {t('payments.missing.totalUnscheduled', { amount: Number.isFinite(totalMissing) ? totalMissing.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '—' })}
              </p>
            </div>
            <button onClick={() => setShowMissing(false)} className="text-amber-400 hover:text-amber-600 text-[14px] font-[700]">✕</button>
          </div>
        </div>
      </ProFeatureGate>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-3">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">{t('payments.kpi.paid')}</div>
          <div className="text-[18px] font-[900] text-[#22C55E]">{formatMoneyValue(totalPaid, isEs)}</div>
          <div className="text-[9px] font-[700] text-[#9E9890] mt-0.5">{paid.length} {t('payments.kpi.paymentsLabel')}</div>
        </div>
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-3">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">{t('payments.kpi.pending')}</div>
          <div className="text-[18px] font-[900] text-[#06539B]">{formatMoneyValue(totalPending, isEs)}</div>
          <div className="text-[9px] font-[700] text-[#9E9890] mt-0.5">{pending.length} {t('payments.kpi.paymentsLabel')}</div>
        </div>
        <div className={`bg-white border rounded-xl p-3 ${totalOverdue > 0 ? 'border-red-300' : 'border-[#ECEAE4]'}`}>
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">{t('payments.kpi.overdue')}</div>
          <div className={`text-[18px] font-[900] ${totalOverdue > 0 ? 'text-red-500' : 'text-[#1C1A17]'}`}>
            {formatMoneyValue(totalOverdue, isEs)}
          </div>
          <div className="text-[9px] font-[700] text-[#9E9890] mt-0.5">{overdue.length} {t('payments.kpi.paymentsLabel')}</div>
        </div>
        <div className="bg-white border border-amber-200 rounded-xl p-3">
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-amber-600 mb-1">{t('payments.kpi.unscheduled')}</div>
          <div className="text-[18px] font-[900] text-amber-600">{formatMoneyValue(totalMissing, isEs)}</div>
          <div className="text-[9px] font-[700] text-[#9E9890] mt-0.5">{missingTerms.length} {t('payments.kpi.contractsLabel')}</div>
        </div>
      </div>

      {/* NBA-13C: SCHEDULED payments shown SEPARATELY from due/paid/overdue. Future
          scheduled rows (created only by a future schedule-payments endpoint) must
          never read as a real obligation — distinct section, distinct copy. */}
      {scheduled.length > 0 && (
        <div data-testid="payments-scheduled-section" className="bg-white border border-[#C9DDF0] rounded-xl p-4">
          <div className="flex items-start gap-2">
            <Info size={14} className="text-[#06539B] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-[12px] font-[800] text-[#1C1A17]">
                {isEs ? 'Pagos programados (no vencidos ni pagados)' : 'Scheduled payments (not due or paid)'}
              </h3>
              <p className="text-[10px] font-[600] text-[#9E9890] mt-0.5 leading-relaxed">
                {isEs
                  ? `${scheduled.length} pago(s) programado(s) · ${formatMoneyValue(totalScheduled, isEs)}. Se muestran por separado: no se cuentan como pendientes, vencidos ni pagados, y no actualizan Presupuesto, Calendario/Gantt ni QuickBooks.`
                  : `${scheduled.length} scheduled payment(s) · ${formatMoneyValue(totalScheduled, isEs)}. Shown separately: not counted as pending, overdue, or paid, and they do not update Budget, Calendar/Gantt, or QuickBooks.`}
              </p>
            </div>
          </div>

          {/* CREATE-FLOW-4: per-row list with a gated Cancel action. The Cancel
              action appears ONLY when the backend readiness probe allows this
              user+project (canCancel) AND the row is a SCHEDULED, DRAFT_SCHEDULE_LINE
              payment. Cancelling is soft (row → CANCELLED, never deleted) and drops
              the row out of this active list on the next refetch. */}
          <div data-testid="payments-scheduled-list" className="mt-3 space-y-1.5">
            {scheduled.map((payment) => {
              const cancellable =
                scheduleReadiness?.canCancel === true &&
                payment.status === 'SCHEDULED' &&
                payment.sourceType === 'DRAFT_SCHEDULE_LINE'
              const isTarget = cancelTargetId === payment.id
              const rowResult = cancelResult?.paymentId === payment.id ? cancelResult : null
              return (
                <div
                  key={payment.id}
                  data-testid={`scheduled-payment-row-${payment.id}`}
                  className="rounded-lg border border-[#E4EDF6] bg-[#F7FAFD] px-2.5 py-2"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-[800] text-[#1C1A17] flex-1 min-w-0 truncate">
                      {payment.milestoneLabel || t(`payments.type.${payment.type}`, { defaultValue: payment.type })}
                    </span>
                    <span className="text-[10px] font-[600] text-[#9E9890] whitespace-nowrap">
                      {payment.dueDate ? formatDate(payment.dueDate) : '—'}
                    </span>
                    <span className="text-[11px] font-[800] text-[#1C1A17] whitespace-nowrap">
                      {formatMoneyValue(payment.amount, isEs)}
                    </span>
                    {cancellable && !isTarget && (
                      <button
                        type="button"
                        data-testid={`scheduled-payment-cancel-btn-${payment.id}`}
                        onClick={() => openCancel(payment.id)}
                        className="text-[10px] font-[800] text-[#B4232A] border border-[#E7C3C5] rounded-md px-2 py-1 whitespace-nowrap"
                      >
                        {isEs ? CF4_CANCEL_COPY.cancelBtn.es : CF4_CANCEL_COPY.cancelBtn.en}
                      </button>
                    )}
                  </div>

                  {/* Inline confirm + required reason (only for the targeted row). */}
                  {cancellable && isTarget && (
                    <div data-testid={`scheduled-payment-cancel-confirm-${payment.id}`} className="mt-2 space-y-1.5">
                      <p className="text-[11px] font-[900] text-[#1C1A17]">
                        {isEs ? CF4_CANCEL_COPY.title.es : CF4_CANCEL_COPY.title.en}
                      </p>
                      <p className="text-[10px] font-[600] text-[#6B655C] leading-relaxed">
                        {isEs ? CF4_CANCEL_COPY.body.es : CF4_CANCEL_COPY.body.en}
                      </p>
                      <label className="block text-[9px] font-[800] uppercase tracking-wide text-[#9E9890]">
                        {isEs ? CF4_CANCEL_COPY.reasonLabel.es : CF4_CANCEL_COPY.reasonLabel.en}
                      </label>
                      <textarea
                        data-testid={`scheduled-payment-cancel-reason-${payment.id}`}
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        rows={2}
                        placeholder={isEs ? CF4_CANCEL_COPY.reasonPlaceholder.es : CF4_CANCEL_COPY.reasonPlaceholder.en}
                        className="w-full rounded-md border border-[#CBD9E8] px-2 py-1.5 text-[11px] font-[600] text-[#1C1A17]"
                      />
                      {rowResult && !rowResult.neutral && (
                        <p className="text-[10px] font-[800] text-[#B4232A]">{rowResult.message}</p>
                      )}
                      <div className="flex flex-col sm:flex-row gap-2">
                        <button
                          type="button"
                          disabled={cancelSubmitting || cancelReason.trim().length === 0}
                          data-testid={`scheduled-payment-cancel-confirm-btn-${payment.id}`}
                          onClick={() => runCancelScheduled(payment)}
                          className="w-full rounded-md border border-[#B4232A] bg-[#B4232A] px-2.5 py-1.5 text-[11px] font-[800] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {cancelSubmitting
                            ? (isEs ? CF4_CANCEL_COPY.cancellingBtn.es : CF4_CANCEL_COPY.cancellingBtn.en)
                            : (isEs ? CF4_CANCEL_COPY.confirmCta.es : CF4_CANCEL_COPY.confirmCta.en)}
                        </button>
                        <button
                          type="button"
                          disabled={cancelSubmitting}
                          data-testid={`scheduled-payment-keep-btn-${payment.id}`}
                          onClick={closeCancel}
                          className="w-full rounded-md border border-[#D6CFC4] bg-white px-2.5 py-1.5 text-[11px] font-[800] text-[#6B655C]"
                        >
                          {isEs ? CF4_CANCEL_COPY.keepCta.es : CF4_CANCEL_COPY.keepCta.en}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Result banner (success / neutral already-cancelled) shown when
                      the inline confirm is closed. */}
                  {rowResult && !isTarget && (
                    <p
                      data-testid={`scheduled-payment-cancel-result-${payment.id}`}
                      className={`mt-1 text-[10px] font-[800] ${rowResult.neutral ? 'text-[#6B655C]' : 'text-[#B4232A]'}`}
                    >
                      {rowResult.message}
                    </p>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Payment timeline by contractor */}
      <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[#ECEAE4]">
          <h3 className="text-[13px] font-[800]">{t('payments.timeline.title')}</h3>
          <p className="text-[10px] font-[600] text-[#9E9890] mt-0.5">
            {t('payments.timeline.subtitle')}
          </p>
          {/* PR-F3: auto-created draw-schedule payments are not user-confirmed. */}
          {hasAutoCreated && (
            <p data-testid="payments-autocreated-note" className="text-[10px] font-[700] text-[#9E6B00] mt-1 flex items-center gap-1">
              <AlertTriangle size={11} className="flex-shrink-0" />
              {isEs
                ? 'Creado automáticamente desde el calendario de pagos del contrato — revisar antes de marcar pagado.'
                : 'Auto-created from contract draw schedule — verify before marking paid.'}
            </p>
          )}
        </div>

        {timelineGroups.map(group => {
          const contractor = group.label
          const contractorPayments = group.rows
          const trade = group.trade
          const total = contractorPayments.reduce((s, p) => s + p.amount, 0)
          const paidAmt = contractorPayments.filter(p => p.status === 'PAID').reduce((s, p) => s + p.amount, 0)
          // PR-F3: denominator-safe % (caps + "—") + NaN-safe clamped bar width.
          const paidPctDisplay = formatPercentRatio(paidAmt, total, { isEs }).displayValue
          const paidPctWidth = Number.isFinite(total) && total > 0
            ? Math.min(100, Math.max(0, (paidAmt / total) * 100))
            : 0

          return (
            <div key={group.key} data-testid={`payments-timeline-group-${group.key}`} className="border-t border-[#ECEAE4] first:border-t-0">
              {/* Contractor header */}
              <div className="flex items-center gap-3 px-4 py-2.5 bg-[#FAFAF8]">
                <div className="flex-1">
                  <span className="text-[12px] font-[800] text-[#1C1A17]">{contractor}</span>
                  <span className="text-[10px] font-[600] text-[#9E9890] ml-2">{trade}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-[60px] h-[4px] bg-[#ECEAE4] rounded-full overflow-hidden">
                    <div className="h-full bg-[#22C55E] rounded-full" style={{ width: `${paidPctWidth}%` }} />
                  </div>
                  <span className="text-[10px] font-[800] text-[#9E9890]">{paidPctDisplay}</span>
                  <span className="text-[11px] font-[800] text-[#1C1A17]">{formatMoneyValue(total, isEs)}</span>
                </div>
              </div>

              {/* Payment draws */}
              {contractorPayments.map((payment, idx) => (
                <PaymentTimelineRow
                  key={payment.id}
                  payment={payment}
                  idx={idx}
                  isEs={isEs}
                  typeLabel={t(`payments.type.${payment.type}`, { defaultValue: payment.type })}
                  sourceLabel={(() => {
                    const src = paymentSource(payment)
                    return src === 'contract' ? t('payments.source.contract')
                      : src === 'quote' ? t('payments.source.quote')
                      : t('payments.source.manual')
                  })()}
                  sourceKind={paymentSource(payment)}
                  paidLabel={t('payments.timeline.paid')}
                  dueLabel={t('payments.timeline.due')}
                  formatDate={formatDate}
                  // D-25AGO-09 — settleability is decided by the SAME two
                  // predicates the server guards with: the status whitelist
                  // (PENDING/OVERDUE only — SCHEDULED must pass through PENDING
                  // first) and the provenance rule (a CF-2 draft-line row belongs
                  // to the flag-gated flow, never to this one). A demo project
                  // has no rows to write.
                  settleable={
                    !isDemoProject(projectId) &&
                    isSettleableStatus(payment.status) &&
                    isSettleableSourceType(payment.sourceType)
                  }
                  isSettleTarget={settleTarget?.id === payment.id}
                  settleError={settleError?.paymentId === payment.id ? settleError.message : null}
                  settleSubmitting={settleSubmitting}
                  onOpenSettle={() => openSettle(payment.id)}
                  onCancelSettle={closeSettle}
                  onConfirmSettle={() => runSettle(payment)}
                />
              ))}
            </div>
          )
        })}
      </div>

      {/* Upcoming payments alert */}
      {pending.length > 0 && (
        <div className="bg-[#06539B]/5 border border-[#06539B]/15 rounded-xl p-4">
          <div className="flex items-center gap-2 text-[12px] font-[800] text-[#06539B] mb-2">
            <Bell size={14} />
            {t('payments.upcoming')}
          </div>
          <div className="space-y-1.5">
            {pending.sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || '')).slice(0, 4).map(p => (
              <div key={p.id} className="flex items-center gap-2 text-[11px]">
                <Calendar size={10} className="text-[#06539B] flex-shrink-0" />
                <span className="font-[700] text-[#1C1A17]">{formatDate(p.dueDate)}</span>
                <span className="text-[#9E9890] font-[600] flex-1 truncate">— {p.contract?.contractorName} ({p.notes?.split('—')[0]?.trim()})</span>
                <span className="font-[800] text-[#1C1A17]">{formatMoneyValue(p.amount, isEs)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
